import React from 'react';
import PublicLayout from '../../components/PublicLayout';
import './TermsOfService.css';

function TermsOfService() {
  return (
    <PublicLayout>
      <div className="terms-container">
        <div className="terms-header">
          <h1>Terms and Conditions</h1>
          <p><strong>Last Updated:</strong> 14th July 2025</p>
        </div>
        
        <div className="terms-content">
          <section>
            <h2>1. Agreement to Terms</h2>
            <p>
              These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Customer," "you," or "your") and Oslith AI Private Limited, operating the Velox AI platform ("Company," "we," "us," or "our") regarding your use of our B2B revenue operations intelligence platform and related services (the "Service").
            </p>
            <p>By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Service.</p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>Velox AI provides a B2B revenue operations intelligence platform that includes:</p>
            <ul>
              <li>AI-powered sales intelligence automation tools</li>
              <li>Customer relationship management support features</li>
              <li>Integration capabilities with third-party business tools</li>
              <li>Training and support resources</li>
            </ul>
          </section>

          <section>
            <h2>3. Account Registration and Eligibility</h2>
            
            <h3>3.1 Eligibility</h3>
            <ul>
              <li>You must be at least 18 years old</li>
              <li>You must have authority to bind your organization</li>
              <li>You must provide accurate and complete information</li>
            </ul>

            <h3>3.2 Account Responsibilities</h3>
            <ul>
              <li>Maintain security of your account credentials</li>
              <li>Notify us immediately of unauthorized access</li>
              <li>Ensure all users comply with these Terms</li>
              <li>You are responsible for all activities under your account</li>
            </ul>
          </section>

          <section>
            <h2>4. Subscription Plans and Billing</h2>
            
            <h3>4.1 Subscription Terms</h3>
            <ul>
              <li>Service is provided on a subscription basis</li>
              <li>Plans may include user limits, feature restrictions, and usage quotas</li>
              <li>Pricing is available on our website and subject to change with notice</li>
            </ul>

            <h3>4.2 Billing and Payment</h3>
            <ul>
              <li>Subscriptions are billed in advance (monthly or annually)</li>
              <li>Payment is due upon invoice date</li>
              <li>All fees are non-refundable except as specified in Section 5</li>
              <li>Late payments may result in service suspension</li>
              <li>You authorize automatic renewal unless cancelled</li>
            </ul>

            <h3>4.3 Taxes and Currency</h3>
            <ul>
              <li>All prices listed in USD, INR, GBP, and EUR unless otherwise specified</li>
              <li>You are responsible for all applicable taxes, duties, and governmental charges in your jurisdiction</li>
              <li>Indian GST applicable for Indian customers as per prevailing rates</li>
              <li>Currency conversion rates determined by payment processor at time of transaction</li>
            </ul>
          </section>

          <section>
            <h2>5. Refund and Cancellation Policy</h2>
            
            <h3>5.1 Cancellation Rights</h3>
            <ul>
              <li>You may cancel your subscription at any time</li>
              <li>Cancellation takes effect at the end of your current billing period</li>
              <li>No refunds for partial billing periods unless specified below</li>
              <li>Data deletion occurs within 30 days after cancellation</li>
            </ul>

            <h3>5.2 Refund Policy</h3>
            
            <p><strong>Monthly Subscriptions:</strong></p>
            <ul>
              <li>No refund shall be provided if the plan has been purchased</li>
              <li>No refund shall be provided in case of monthly subscription renewal</li>
            </ul>

            <p><strong>Annual Subscriptions:</strong></p>
            <ul>
              <li>Pro-rated refund for unused portion if cancelled within 15 days</li>
              <li>No refunds shall be provided after 30 days from annual renewal date</li>
              <li>Must request refund via email to velox-ai@outlook.com</li>
            </ul>

            <p><strong>Service Disruptions:</strong></p>
            <ul>
              <li>Service credit for extended downtime (&gt;4 hours) not due to scheduled maintenance</li>
              <li>Credits calculated as pro-rated daily rate for affected period</li>
            </ul>

            <p><strong>Exceptional Circumstances:</strong></p>
            <ul>
              <li>Refunds may be considered for extraordinary circumstances</li>
              <li>Refunds are subject to management approval on case-by-case basis</li>
            </ul>

            <h3>5.3 Refund Timeline</h3>
            <ul>
              <li>Refund requests are processed within 5-7 business days</li>
              <li>Refunds are issued to original payment method within 7-14 business days</li>
              <li>Processing time may vary by country and payment method</li>
              <li>International refunds may take longer due to banking processes</li>
              <li>Refund amounts are subject to currency conversion rates at time of processing</li>
            </ul>

            <h3>5.4 Non-Refundable Items</h3>
            <ul>
              <li>Setup fees or implementation costs</li>
              <li>Third-party integration fees</li>
              <li>Custom development work</li>
              <li>Training and consulting services</li>
            </ul>
          </section>

          <section>
            <h2>6. Acceptable Use Policy</h2>
            
            <h3>6.1 Permitted Uses</h3>
            <ul>
              <li>Use Service for legitimate business purposes</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Respect intellectual property rights</li>
            </ul>

            <h3>6.2 Prohibited Uses</h3>
            <ul>
              <li>Illegal activities or content</li>
              <li>Harassment, abuse, or harmful behavior</li>
              <li>Unauthorized access to systems or data</li>
              <li>Reverse engineering or creating derivative works</li>
              <li>Spam or unsolicited communications</li>
              <li>Interfering with Service functionality</li>
            </ul>
          </section>

          <section>
            <h2>7. Data and Privacy</h2>
            
            <h3>7.1 Customer Data</h3>
            <ul>
              <li>You retain ownership of your data</li>
              <li>We process data according to our Privacy Policy</li>
              <li>You warrant you have rights to data you provide</li>
              <li>We may use aggregated, anonymized data for improvements</li>
            </ul>

            <h3>7.2 Data Security</h3>
            <ul>
              <li>We implement industry-standard security measures</li>
              <li>You are responsible for data access controls</li>
              <li>Report security incidents immediately</li>
            </ul>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            
            <h3>8.1 Service Ownership</h3>
            <ul>
              <li>We retain all rights to the Service and our technology</li>
              <li>You receive a limited license to use the Service</li>
              <li>License terminates upon account closure</li>
            </ul>

            <h3>8.2 Customer Content</h3>
            <ul>
              <li>You retain ownership of your content</li>
              <li>You grant us license to process content to provide Service</li>
              <li>You warrant content doesn't infringe third-party rights</li>
            </ul>
          </section>

          <section>
            <h2>9. Service Availability and Support</h2>
            
            <h3>9.1 Service Level</h3>
            <ul>
              <li>Target uptime of 99.9% (excluding scheduled maintenance)</li>
              <li>Scheduled maintenance with advance notice</li>
              <li>Best effort to minimize service disruptions</li>
            </ul>

            <h3>9.2 Support</h3>
            <ul>
              <li>Email support during Indian Standard Time (IST) business hours (9:00 AM - 6:00 PM IST)</li>
              <li>Extended support hours for premium plans</li>
              <li>Documentation and self-help resources available 24/7</li>
              <li>Emergency support for critical issues</li>
            </ul>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul>
              <li><strong>Service Provided "As Is": </strong>The Service is provided without warranties of any kind, either express or implied</li>
              <li><strong>Liability Cap: </strong>Our total liability is limited to the amount you paid us in the 12 months preceding the claim</li>
              <li><strong>Excluded Damages: </strong>We are not liable for indirect, consequential, punitive, or special damages</li>
              <li><strong>No Warranties: </strong>We disclaim all warranties including merchantability, fitness for purpose, and non-infringement</li>
            </ul>
            <p><strong>Exception:</strong> Nothing in this section limits liability for death, personal injury, fraud, or other matters that cannot be legally limited.</p>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>You agree to indemnify and defend us against claims arising from:</p>
            <ul>
              <li>Your use of the Service</li>
              <li>Violation of these Terms</li>
              <li>Infringement of third-party rights</li>
              <li>Your data or content</li>
            </ul>
          </section>

          <section>
            <h2>12. Termination</h2>
            
            <h3>12.1 Termination by You</h3>
            <ul>
              <li>Cancel subscription at any time</li>
              <li>Account access continues until end of billing period</li>
            </ul>

            <h3>12.2 Termination by Us</h3>
            <ul>
              <li>For breach of Terms (with cure period if applicable)</li>
              <li>Non-payment after reasonable notice</li>
              <li>Violation of Acceptable Use Policy</li>
              <li>Discontinuation of Service (with notice)</li>
            </ul>

            <h3>12.3 Effect of Termination</h3>
            <ul>
              <li>Immediate cessation of Service access</li>
              <li>Data deletion after 30-day grace period</li>
              <li>Survival of payment obligations and legal provisions</li>
            </ul>
          </section>

          <section>
            <h2>13. Changes to Terms</h2>
            <p>We may modify these Terms with reasonable notice. Continued use after changes constitutes acceptance. Material changes will be communicated via email or Service notification.</p>
          </section>

          <section>
            <h2>14. Dispute Resolution</h2>
            
            <h3>14.1 Governing Law</h3>
            <p>These Terms are governed by the laws of India.</p>

            <h3>14.2 Dispute Process</h3>
            <ul>
              <li><strong>First Step:</strong> Contact us at velox-ai@outlook.com to resolve any disputes informally</li>
              <li><strong>If unresolved:</strong> Disputes will be resolved through binding arbitration in Delhi, India under Indian law</li>
              <li><strong>Court jurisdiction:</strong> The courts of Delhi, India have jurisdiction for any matters that cannot be arbitrated</li>
            </ul>

            <h3>14.3 Exceptions</h3>
            <p>The following disputes may be brought in court instead of arbitration:</p>
            <ul>
              <li>Requests for emergency relief</li>
              <li>Intellectual property disputes</li>
              <li>Small claims (under applicable limits)</li>
            </ul>
          </section>

          <section>
            <h2>15. General Provisions</h2>
            
            <h3>15.1 Entire Agreement</h3>
            <p>These Terms, Privacy Policy, and Service documentation constitute the entire agreement.</p>

            <h3>15.2 Severability</h3>
            <p>If any provision is unenforceable, remaining provisions continue in effect.</p>

            <h3>15.3 Force Majeure</h3>
            <p>We are not liable for performance delays due to circumstances beyond our control.</p>

            <h3>15.4 Assignment and Transfer</h3>
            <p>You may not assign these Terms without our written consent. We may assign our rights and obligations without restriction, including to affiliates or in connection with business transfers. Cross-border assignments comply with applicable data protection laws.</p>

            <h3>15.5 Waiver</h3>
            <p>Failure to enforce any provision doesn't waive future enforcement.</p>
          </section>

          <section>
            <h2>16. Contact Information</h2>
            <p><strong>Email:</strong> velox-ai@outlook.com</p>
          </section>
        </div>
      </div>
    </PublicLayout>
  );
}

export default TermsOfService; 